import EventManager, {
  EventManagerConfiguration,
} from '../__internals__/EventManager'
import baseTrackView from './trackView'
import baseTrackEvent from './trackEvent'
import baseTrackCustomEvent from './trackCustomEvent'
import baseTrackClick from './trackClick'
import baseTrackTaskInit from './trackTaskInit'
import baseTrackTaskDone from './trackTaskDone'

// I didn't want to have to make a class here, but VS Code intellisense from JSDoc does not show
// any values if you jsdoc the properties returned from useAnalytics(). Creating a class here is
// actually decently clean, and it allows the intellisense to continue working for these functions.
class Analytics {
  constructor(
    eventManagerConfiguration: EventManagerConfiguration,
    loginId?: string,
  ) {
    this.eventManager = new EventManager(eventManagerConfiguration, loginId)
    this.trackView = baseTrackView(this.eventManager)
    this.trackCustomEvent = baseTrackCustomEvent(this.eventManager)
    this.trackClick = baseTrackClick(this.eventManager)
    this.trackEvent = baseTrackEvent(this.eventManager)
    this.trackTaskInit = baseTrackTaskInit(this.eventManager)
    this.trackTaskDone = baseTrackTaskDone(this.eventManager)
    this.loginId = loginId
  }

  loginId?: string
  eventManager: EventManager

  /** Sends location path events to Firefly Internal Insights.
   *
   * @param pathname
   * @param eventManagerConfiguration
   * @example
   * const TrackedRoute = () => {
   *   const { trackView } = useAnalytics()
   *   useEffect(() => {
   *     trackView('/demo')
   *   }, [])
   *   return <SomeContent />
   * }
   */
  trackView: ReturnType<typeof baseTrackView>

  /** Sends click events to Firefly Internal Insights.
   *
   * @param nodeId
   * @example
   * const TrackedButton = () => {
   *   const { trackClick } = useAnalytics()
   *   const handleClick = () => {
   *     trackClick('demo-button')
   *   }
   *   return <Button onClick={handleClick}>Click Me</Button>
   * }
   */
  trackClick: ReturnType<typeof baseTrackClick>

  /** Sends custom events to Firefly Internal Insights.
   *
   * @param type
   * @param key
   * @param value
   * @example
   * const CustomComponent = ({ companyName }) => {
   *   const { trackEvent } = useAnalytics()
   *   useEffect(() => {
   *     if (companyName !== 'Target Corporation') {
   *       trackEvent('external-user', 'company', companyName)
   *     }
   *   }, [ company ])
   *   return <div>{companyName}</div>
   * }
   */
  trackCustomEvent: ReturnType<typeof baseTrackCustomEvent>

  /**
   * Sends an event to Firefly Internal Insights for tracking.
   * @param eventManagerConfiguration
   * @example
   * const MyComponent = (props) => {
   *   const { trackEvent } = useAnalytics()
   *   useEffect(() => {
   *     trackEvent({
   *       application: {
   *         name: 'my-app',
   *       }
   *     })
   *   }, [ props.trigger ])
   *   return <div>Hello World</div>
   * }
   */
  trackEvent: ReturnType<typeof baseTrackEvent>

  /** Starts tracking a Task with Firefly Internal Insights.
   * @param taskName
   * @example
   * const TrackTaskStart = () => {
   *   const { trackTaskInit } = useAnalytics()
   *   useEffect(() => {
   *     trackTaskInit('task 1')
   *   }, [])
   *   return <SomeContent />
   * }
   */
  trackTaskInit: ReturnType<typeof baseTrackTaskInit>

  /** Stops the current tracked Task in Firefly Internal Insights.
   * @param taskName
   * @param taskCompletionStatus
   * @example
   * const TrackedRoute = () => {
   *   const { trackTaskDone } = useAnalytics()
   *   useEffect(() => {
   *     trackTaskDone({ name: 'Task 1', status: 'success' })
   *   }, [])
   *   return <SomeContent />
   * }
   */
  trackTaskDone: ReturnType<typeof baseTrackTaskDone>
}

export default Analytics
